import { useMemo, useState } from "react";
import { marked } from "marked";
import {
  TextB, TextItalic, TextH, ListBullets, ListNumbers, Link as LinkIcon,
  Image as ImageIcon, Code, Quotes, Eye, PencilSimple,
} from "@phosphor-icons/react";
import MediaBrowser from "./MediaBrowser";

marked.setOptions({ gfm: true, breaks: true });

/**
 * Lightweight markdown rich editor.
 *
 * Why markdown: `cms.pages.body_md` + `cms.announcements.body_md` are
 * stored as markdown. Editorial staff see a toolbar that inserts tokens
 * for them, plus a live Preview tab rendered with `marked`.
 *
 * The media picker opens a sheet against the cms-media bucket and
 * inserts either a Markdown image or link at the caret position.
 */
export default function MarkdownEditor({
  value,
  onChange,
  organizationId,
  placeholder = "Start writing…",
  testIdPrefix = "md-editor",
  minRows = 10,
}) {
  const [mode, setMode] = useState("write"); // 'write' | 'preview'
  const [picker, setPicker] = useState(null); // { kind: 'image' | 'link' }
  const [textarea, setTextarea] = useState(null);

  const html = useMemo(() => {
    try {
      return marked.parse(value || "");
    } catch {
      return "";
    }
  }, [value]);

  const surround = (left, right = left) => {
    const ta = textarea;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = value.slice(0, start);
    const selected = value.slice(start, end) || "text";
    const after = value.slice(end);
    const next = `${before}${left}${selected}${right}${after}`;
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + left.length, start + left.length + selected.length);
    });
  };

  const prefixLine = (prefix) => {
    const ta = textarea;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = value.slice(0, start);
    const lastNl = before.lastIndexOf("\n");
    const lineStart = lastNl === -1 ? 0 : lastNl + 1;
    const next = `${value.slice(0, lineStart)}${prefix}${value.slice(lineStart)}`;
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + prefix.length, end + prefix.length);
    });
  };

  const insertAtCaret = (snippet) => {
    const ta = textarea;
    const pos = ta ? ta.selectionEnd : value.length;
    const next = value.slice(0, pos) + snippet + value.slice(pos);
    onChange(next);
    requestAnimationFrame(() => {
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(pos + snippet.length, pos + snippet.length);
    });
  };

  const onPickImage = (publicMarkdown) => {
    insertAtCaret(publicMarkdown);
    setPicker(null);
  };

  return (
    <div
      data-testid={testIdPrefix}
      className="overflow-hidden rounded-2xl border border-neutral-200 bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-1 border-b border-neutral-200 bg-neutral-50 px-2 py-1.5">
        <div className="flex flex-wrap items-center gap-0.5">
          <ToolbarButton testId={`${testIdPrefix}-bold`} label="Bold" onClick={() => surround("**")}>
            <TextB size={14} weight="bold" />
          </ToolbarButton>
          <ToolbarButton testId={`${testIdPrefix}-italic`} label="Italic" onClick={() => surround("_")}>
            <TextItalic size={14} weight="bold" />
          </ToolbarButton>
          <ToolbarButton testId={`${testIdPrefix}-h2`} label="Heading" onClick={() => prefixLine("## ")}>
            <TextH size={14} weight="bold" />
          </ToolbarButton>
          <Divider />
          <ToolbarButton testId={`${testIdPrefix}-bullet`} label="Bullet list" onClick={() => prefixLine("- ")}>
            <ListBullets size={14} weight="bold" />
          </ToolbarButton>
          <ToolbarButton testId={`${testIdPrefix}-ol`} label="Numbered list" onClick={() => prefixLine("1. ")}>
            <ListNumbers size={14} weight="bold" />
          </ToolbarButton>
          <ToolbarButton testId={`${testIdPrefix}-quote`} label="Quote" onClick={() => prefixLine("> ")}>
            <Quotes size={14} weight="bold" />
          </ToolbarButton>
          <ToolbarButton testId={`${testIdPrefix}-code`} label="Code" onClick={() => surround("`")}>
            <Code size={14} weight="bold" />
          </ToolbarButton>
          <Divider />
          <ToolbarButton
            testId={`${testIdPrefix}-link`}
            label="Link"
            onClick={() => {
              const url = window.prompt("Link URL:");
              if (!url) return;
              surround("[", `](${url})`);
            }}
          >
            <LinkIcon size={14} weight="bold" />
          </ToolbarButton>
          <ToolbarButton
            testId={`${testIdPrefix}-image`}
            label="Insert image"
            onClick={() => setPicker({ kind: "image" })}
          >
            <ImageIcon size={14} weight="bold" />
          </ToolbarButton>
        </div>
        <div className="flex items-center gap-0.5 rounded-full border border-neutral-200 bg-white p-0.5">
          <TabButton
            testId={`${testIdPrefix}-tab-write`}
            active={mode === "write"}
            onClick={() => setMode("write")}
          >
            <PencilSimple size={11} weight="bold" />
            Write
          </TabButton>
          <TabButton
            testId={`${testIdPrefix}-tab-preview`}
            active={mode === "preview"}
            onClick={() => setMode("preview")}
          >
            <Eye size={11} weight="bold" />
            Preview
          </TabButton>
        </div>
      </div>

      {mode === "write" ? (
        <textarea
          ref={setTextarea}
          id={`${testIdPrefix}-textarea-input`}
          data-testid={`${testIdPrefix}-textarea`}
          aria-label="Content in Markdown"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={minRows}
          className="w-full resize-y px-4 py-3 font-mono text-sm leading-relaxed text-neutral-900 focus:outline-none"
        />
      ) : (
        <div
          data-testid={`${testIdPrefix}-preview`}
          className="prose prose-sm max-w-none px-4 py-4 prose-headings:font-heading prose-headings:font-semibold prose-headings:tracking-tight"
          dangerouslySetInnerHTML={{ __html: html || "<p class='text-neutral-400'>Nothing to preview yet.</p>" }}
        />
      )}

      {picker?.kind === "image" && (
        <MediaBrowser
          organizationId={organizationId}
          onPick={(f, url) => onPickImage(`\n![${f.display_name || f.name}](${url})\n`)}
          onClose={() => setPicker(null)}
          mode="pick"
        />
      )}
    </div>
  );
}

function ToolbarButton({ children, onClick, label, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={label}
      title={label}
      onClick={onClick}
      className="rounded-md p-1.5 text-neutral-600 hover:bg-white hover:text-neutral-900"
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-1 h-5 w-px bg-neutral-200" />;
}

function TabButton({ children, active, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider transition-all ${
        active ? "bg-primary text-white" : "text-neutral-500 hover:text-neutral-900"
      }`}
    >
      {children}
    </button>
  );
}
